import { SelectInputOption } from "apps/user/learner/api/types";

// Count and duration questions are answered with a stepper but scored against
// authored options ("1-7 drinks", "20 mins - 1 hr 14 mins"). Each option
// carries the inclusive lower bound of its range as data, resolved
// server-side at import — in items for a count, minutes for a duration.
//
// Buckets are contiguous, so the option an answer belongs to is the highest
// bound it still clears. Nothing here reads the wording, which is translated
// and reworded.
export const bucketFor = (
  amount: number,
  options: SelectInputOption[]
): SelectInputOption | null => {
  const buckets = options
    .filter(
      (option): option is SelectInputOption & { lowerBound: number } =>
        option.lowerBound != null
    )
    .sort((a, b) => b.lowerBound - a.lowerBound);

  return (
    buckets.find((option) => amount >= option.lowerBound) ??
    buckets[buckets.length - 1] ??
    null
  );
};

export const formatDuration = (hours: number, minutes: number): string => {
  const parts: string[] = [];
  if (hours > 0) parts.push(`${hours} ${hours === 1 ? "hr" : "hrs"}`);
  if (minutes > 0) parts.push(`${minutes} mins`);
  return parts.length ? parts.join(" ") : "0 mins";
};

// Inverse of formatDuration — parses the learner's stored pick back into
// hours/minutes so revisiting a duration question re-seeds the stepper with
// exactly what they picked, not the bucket it scored into.
//
// This reads a string formatDuration wrote, not authored copy, so it is
// unaffected by translation — which is also why formatDuration's units stay
// English: the two are a private wire format, not display text.
export const parseDurationResponse = (
  raw: string
): { hours: number; minutes: number } => {
  const hoursMatch = raw.match(/(\d+)\s*hrs?/i);
  const minutesMatch = raw.match(/(\d+)\s*mins?/i);
  return {
    hours: hoursMatch ? parseInt(hoursMatch[1], 10) : 0,
    minutes: minutesMatch ? parseInt(minutesMatch[1], 10) : 0,
  };
};
